import React, { useEffect, useMemo, useState } from "react";
import axios from "../services/axiosInstance";
import styles from "../components/Modals/Modal.module.css"; 

export default function FasesTimeline({ pautaId, tipoMateriaId, historico = [] }) {
  const [fluxo, setFluxo] = useState([]);
  const [busy, setBusy] = useState(false);

  // Normaliza strings
  const normalizar = (s = "") =>
    String(s).normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

  const formatarNumeroSessao = (numeroRaw) => {
    if (!numeroRaw) return "";
    const num = typeof numeroRaw === "string" ? numeroRaw.split("/")[0] : String(numeroRaw);
    return `${parseInt(num, 10)}ª`;
  };

  // 1. Carrega o fluxo Teórico
  useEffect(() => {
    (async () => {
      if (!tipoMateriaId) {
        setFluxo([]);
        return;
      }
      setBusy(true);
      try {
        const { data } = await axios.get(`/tipos-status-materia/tipo/${tipoMateriaId}`);
        const linhas = (Array.isArray(data) ? data : []).sort(
          (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)
        );
        setFluxo(linhas);
      } catch (error) {
        console.error("Erro ao carregar fluxo:", error);
        setFluxo([]);
      } finally {
        setBusy(false);
      }
    })();
  }, [tipoMateriaId]);

  // 2. Ordena o histórico
  const historicoOrdenado = useMemo(() => {
    const arr = Array.isArray(historico) ? [...historico] : [];
    return arr.sort((a, b) => new Date(a.dataCriacao || 0) - new Date(b.dataCriacao || 0));
  }, [historico]);

  // --- ESTILOS VISUAIS ---
  const palette = {
    passado: "#22c55e", 
    atual: "#3b82f6",    
    trilha: "#e5e7eb",
    cardBg: "#ffffff",
    atualBg: "#f0f7ff",
    sessaoBadge: "#e0e7ff",
    legendaBg: "#f3f4f6", 
    legendaText: "#4b5563"
  };

  if (busy && !historicoOrdenado.length) {
    return <div style={{ padding: 10,  color: "#666" }}>Carregando timeline...</div>;
  }

  return (
    <>
      <div className={styles.pautaGrupo} style={{ marginTop: 8, paddingBottom: 16 }}>
        {historicoOrdenado.length === 0 ? (
           <div style={{ textAlign: 'center', padding: 20, color: "#6b7280", }}>
              Nenhum histórico de tramitação registrado.
           </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10 }}>
            {historicoOrdenado.map((evento, index) => {
              const isAtual = index === historicoOrdenado.length - 1;
              const cor = isAtual ? palette.atual : palette.passado;
              const bg = isAtual ? palette.atualBg : palette.cardBg;
              const border = isAtual ? `1px solid #bfdbfe` : `1px solid #e5e7eb`;

              // BUSCA A DEFINIÇÃO NO FLUXO
              const faseDefinida = fluxo.find(f => {
                const nomeFase = f.statusTipo?.nome || f.statusNome;
                return normalizar(nomeFase) === normalizar(evento.statusNovo);
              });

              // >>> AQUI ESTAVA O ERRO: O campo na entidade é 'faseDescricao' <<<
              const descricaoParaExibir = faseDefinida?.faseDescricao || null;

              return (
                <div key={evento.id || index} style={{ position: "relative", display: "flex", gap: 8, paddingBottom: 5 }}>
                  
                  {/* Linha Conectora */}
                  {index < historicoOrdenado.length - 1 && (
                    <div
                      style={{
                        position: "absolute",
                        left: 9,
                        top: 24,
                        bottom: -10,
                        width: 2,
                        background: palette.trilha,
                        zIndex: 0,
                      }}
                    />
                  )}

                  {/* Bolinha */}
                  <div style={{ zIndex: 1, paddingTop: 4 }}>
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: "#fff",
                        border: `3px solid ${cor}`,
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        color: cor
                      }}
                    >
                      {index + 1}
                    </div>
                  </div>

                  {/* Card */}
                  <div
                    style={{
                      flex: 1,
                      background: bg,
                      border: border,
                      borderRadius: 8,
                      padding: "8px 12px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <strong style={{  color: "#1f2937" }}>
                        {evento.statusNovo || "Status indefinido"}
                      </strong>
                      {isAtual && (
                        <span style={{ fontWeight: 700, color: palette.atual, textTransform: "uppercase", border: `1px solid ${palette.atual}`, padding: "1px 4px", borderRadius: 4 }}>
                          Atual
                        </span>
                      )}
                    </div>

                    {/* Exibe Descrição da Fase (vinda do Fluxo) */}
                    {descricaoParaExibir && (
                        <div style={{ color: "#4b5563", marginTop: 2, lineHeight: "1.3" }}>
                            {descricaoParaExibir}
                        </div>
                    )}

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4, alignItems: "center" }}>
                      {evento.dataCriacao && (
                        <span style={{ color: "#6b7280" }}>
                          📅 {new Date(evento.dataCriacao).toLocaleString("pt-BR", { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'})}
                        </span>
                      )}
                      {evento.sessao && (
                        <span style={{ 
                            background: palette.sessaoBadge, 
                            color: "#3730a3",
                            padding: "2px 8px", 
                            borderRadius: 12,
                            fontWeight: 500
                        }}>
                          🏛️ {formatarNumeroSessao(evento.sessao.numero)} Sessão {evento.sessao.tipo}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Legenda do Fluxo (Rodapé) */}
      {fluxo.length > 0 && (
          <div style={{ marginTop: 12, padding: "0 4px" }}>
            <h5 style={{ color: "#6b7280", marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Regime Ordinário:
            </h5>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                {fluxo.map((fase, i) => (
                    <div key={fase.id} style={{ display: "flex", alignItems: "center" }}>
                        <div style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "4px 10px",
                            background: palette.legendaBg,
                            borderRadius: 6,
                            color: palette.legendaText,
                            border: "1px solid #e5e7eb"
                        }}>
                            <span style={{ fontWeight: "bold", color: "#9ca3af", minWidth: 14, textAlign: "center" }}>
                                {i + 1}
                            </span>
                            {fase.statusTipo?.descricao || fase.statusNome}
                        </div>
                        {i < fluxo.length - 1 && (
                            <span style={{ marginLeft: 6, color: "#d1d5db" }}>➝</span>
                        )}
                    </div>
                ))}
            </div>
        </div>
      )}
    </>
  );
}